import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  lodgingMode,
  transportMode,
  travelerFields,
  type BookingConfig,
  type BookingOption,
  type TravelerFields,
} from '@travel/contracts';
import { unwrapRecord, type AdminApiClient, type JsonRecord } from './api-client';
import './booking-config-editor.css';

export function BookingConfigEditor({
  api,
  productId,
  value: config,
  onChange: setConfig,
  disabled = false,
}: {
  api: AdminApiClient;
  productId: string;
  value: BookingConfig;
  onChange: (value: BookingConfig) => void;
  disabled?: boolean;
}) {
  const [media, setMedia] = useState<{ url: string; name: string }[]>([]);
  const [appKey, setAppKey] = useState('');
  const [mediaError, setMediaError] = useState('');
  const [uploading, setUploading] = useState(false);
  const lodgingDraft = useRef(config.accommodation);
  const vehicleDrafts = useRef<Partial<Record<'AUTO' | 'CHOICE', BookingOption[]>>>({
    [transportMode(config)]: config.vehicles,
  });
  const appId = sessionStorage.getItem('travel.admin.targetAppId') ?? '';
  async function loadMedia() {
    const product = unwrapRecord(
      await api.get<JsonRecord>(`/api/admin/m2/products/${encodeURIComponent(productId)}`),
    );
    setMedia(
      (Array.isArray(product.media) ? (product.media as JsonRecord[]) : [])
        .filter((m) => m.mediaType === 'IMAGE' && m.status === 'ACTIVE')
        .map((m, i) => ({ url: String(m.url), name: String(m.altText || `线路图片 ${i + 1}`) })),
    );
  }
  useEffect(() => {
    void loadMedia().catch(() => setMediaError('线路图库读取失败，可稍后刷新重试'));
    if (appId)
      void api
        .get<JsonRecord>(`/api/admin/m2/apps/${encodeURIComponent(appId)}`)
        .then((data) =>
          setAppKey(
            String(
              (unwrapRecord(data).app as JsonRecord | undefined)?.appKey ??
                unwrapRecord(data).appKey ??
                '',
            ),
          ),
        )
        .catch(() => undefined);
  }, [api, productId, appId]);
  const thumbnail = (url: string) =>
    url.startsWith('/api/customer/public/m2/media/')
      ? `${import.meta.env.VITE_API_BASE_URL ?? ''}${url}?appContext=${encodeURIComponent(appKey)}`
      : url;
  const imagePicker = (label: string, url: string, change: (url: string) => void) => (
    <div className="booking-media-picker">
      {url && <img src={thumbnail(url)} alt={label} />}
      <label>
        {label}
        <select value={url} onChange={(e) => change(e.target.value)}>
          <option value="">{label === '下单页头图' ? '使用线路主图' : '不设置配图'}</option>
          {media.map((m) => (
            <option key={m.url} value={m.url}>
              {m.name}
            </option>
          ))}
          {url && !media.some((m) => m.url === url) && <option value={url}>当前配图</option>}
        </select>
      </label>
      <label className="booking-upload">
        {uploading ? '优化并上传中…' : '上传图片（自动优化）'}
        <input
          type="file"
          accept="image/png,image/jpeg"
          disabled={disabled || uploading || !appId}
          onChange={async (e) => {
            const input = e.currentTarget;
            const file = input.files?.[0];
            if (!file) return;
            if (!['image/png', 'image/jpeg'].includes(file.type) || file.size > 10 * 1024 * 1024) {
              setMediaError('请选择 10MB 内的 JPG / PNG 图片');
              return;
            }
            setUploading(true);
            setMediaError('');
            try {
              const form = new FormData();
              form.append('file', file);
              form.append('altText', label);
              form.append('sortOrder', String(media.length + 1));
              const result = unwrapRecord(
                await api.upload<JsonRecord>(
                  `/api/admin/m2/apps/${encodeURIComponent(appId)}/products/${encodeURIComponent(productId)}/media/upload`,
                  form,
                  crypto.randomUUID(),
                ),
              );
              await loadMedia();
              if (typeof result.url === 'string') change(result.url);
            } catch (error) {
              setMediaError(error instanceof Error ? error.message : '上传失败');
            } finally {
              setUploading(false);
              input.value = '';
            }
          }}
        />
      </label>
    </div>
  );
  const lodging = config.lodging ?? {
    mode: lodgingMode(config),
    summary: config.accommodation[0]?.description ?? '',
    nights: 1,
    roomCapacity: 2,
  };
  const transport = config.transport ?? {
    mode: transportMode(config),
    summary: config.vehicles[0]?.title ?? '',
  };
  const travelerEditor = (child: boolean) => {
    const fields = travelerFields(config, child);
    return (
      <div className="booking-field-rule">
        <strong>{child ? '儿童' : '成人'}</strong>
        {(['name', 'document', 'mobile'] as const)
          .filter((key) => !child || key !== 'mobile')
          .map((key) => (
            <label className="booking-editor__check" key={key}>
              <input
                type="checkbox"
                checked={fields[key]}
                onChange={(e) => {
                  const next: TravelerFields = { ...fields, [key]: e.target.checked };
                  setConfig(
                    child
                      ? { ...config, childTravelers: { ...next, mobile: false } }
                      : { ...config, travelers: next },
                  );
                }}
              />
              {{ name: '姓名', document: '身份证 / 护照', mobile: '手机号' }[key]}
            </label>
          ))}
        <span>{child ? '儿童联系使用订单联系人' : '仅勾选履约必需的信息'}</span>
      </div>
    );
  };
  return (
    <section className="booking-editor booking-editor--integrated">
      <h2>服务与下单规则</h2>
      <fieldset disabled={disabled}>
        <Row title="人数与儿童">
          <label className="booking-editor__check">
            <input
              type="checkbox"
              checked={config.children.enabled}
              onChange={(e) =>
                setConfig({
                  ...config,
                  children: { ...config.children, enabled: e.target.checked },
                })
              }
            />
            允许儿童预订
          </label>
          {config.children.enabled && (
            <>
              <Money
                label="儿童基础价（元 / 人）"
                value={config.children.priceCents}
                onChange={(priceCents) =>
                  setConfig({ ...config, children: { ...config.children, priceCents } })
                }
                placeholder="与成人同价"
              />
              <label>
                年龄下限
                <input
                  type="number"
                  min="0"
                  max="17"
                  value={config.children.minAge ?? ''}
                  placeholder="未限定"
                  onChange={(e) => {
                    const children = { ...config.children };
                    if (e.target.value === '') delete children.minAge;
                    else children.minAge = Number(e.target.value);
                    setConfig({ ...config, children });
                  }}
                />
              </label>
              <label>
                年龄上限
                <input
                  type="number"
                  min="0"
                  max="17"
                  value={config.children.maxAge ?? ''}
                  placeholder="未限定"
                  onChange={(e) => {
                    const children = { ...config.children };
                    if (e.target.value === '') delete children.maxAge;
                    else children.maxAge = Number(e.target.value);
                    setConfig({ ...config, children });
                  }}
                />
              </label>
              <label className="booking-editor__grow">
                儿童费用说明
                <input
                  value={config.children.description}
                  maxLength={200}
                  placeholder="如：不占床，不含早餐"
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      children: { ...config.children, description: e.target.value },
                    })
                  }
                />
              </label>
            </>
          )}
        </Row>
        <Row title="必要信息采集">
          {travelerEditor(false)}
          {config.children.enabled && travelerEditor(true)}
        </Row>
        <Row title="接送与集合">
          <label>
            服务方式
            <select
              value={config.pickup.mode}
              onChange={(e) =>
                setConfig({
                  ...config,
                  pickup: {
                    ...config.pickup,
                    mode: e.target.value as BookingConfig['pickup']['mode'],
                    collectTime: false,
                  },
                })
              }
            >
              <option value="NONE">不提供接送</option>
              <option value="MEETING_POINT">游客自行集合</option>
              <option value="HOTEL">酒店 / 地址接送</option>
            </select>
          </label>
          {config.pickup.mode !== 'NONE' && (
            <label className="booking-editor__grow">
              {config.pickup.mode === 'MEETING_POINT' ? '集合地点与时间' : '接送范围'}
              <input
                value={config.pickup.description}
                placeholder={
                  config.pickup.mode === 'MEETING_POINT'
                    ? '如：08:00 景区东门集合'
                    : '如：景洪市区酒店免费接送'
                }
                onChange={(e) =>
                  setConfig({
                    ...config,
                    pickup: { ...config.pickup, description: e.target.value },
                  })
                }
              />
            </label>
          )}
          {config.pickup.mode === 'HOTEL' && (
            <label className="booking-editor__check">
              <input
                type="checkbox"
                checked={config.pickup.collectTime}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    pickup: { ...config.pickup, collectTime: e.target.checked },
                  })
                }
              />
              需要游客选择期望接送时间
            </label>
          )}
        </Row>
        <Row title="住宿">
          <label>
            住宿方式
            <select
              value={lodging.mode}
              onChange={(e) => {
                if (lodging.mode === 'CHOICE') lodgingDraft.current = config.accommodation;
                setConfig({
                  ...config,
                  lodging: { ...lodging, mode: e.target.value as typeof lodging.mode },
                  accommodation:
                    e.target.value === 'NONE' || e.target.value === 'INCLUDED'
                      ? []
                      : lodgingDraft.current,
                });
              }}
            >
              <option value="NONE">不含住宿</option>
              <option value="INCLUDED">固定住宿，已包含</option>
              <option value="CHOICE">可选住宿方案</option>
            </select>
          </label>
          {lodging.mode !== 'NONE' && (
            <>
              <label>
                住宿晚数
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={lodging.nights}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      lodging: { ...lodging, nights: Number(e.target.value) },
                    })
                  }
                />
              </label>
              <label>
                每间入住人数
                <input
                  type="number"
                  min="1"
                  max="8"
                  value={lodging.roomCapacity}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      lodging: { ...lodging, roomCapacity: Number(e.target.value) },
                    })
                  }
                />
              </label>
              <label className="booking-editor__grow">
                住宿说明
                <input
                  value={lodging.summary}
                  placeholder="如：舒适型酒店标准间"
                  onChange={(e) =>
                    setConfig({ ...config, lodging: { ...lodging, summary: e.target.value } })
                  }
                />
              </label>
              {config.children.enabled && (
                <>
                  <label>
                    儿童占床
                    <select
                      value={config.children.bed ?? 'NONE'}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          children: {
                            ...config.children,
                            bed: e.target.value as 'NONE' | 'ADULT_PRICE' | 'SUPPLEMENT',
                          },
                        })
                      }
                    >
                      <option value="NONE">不提供占床选择</option>
                      <option value="ADULT_PRICE">占床按成人价</option>
                      <option value="SUPPLEMENT">占床补差价</option>
                    </select>
                  </label>
                  {config.children.bed === 'SUPPLEMENT' && (
                    <Money
                      label="每名儿童占床补差（元）"
                      value={config.children.bedSupplementCents ?? 0}
                      onChange={(bedSupplementCents) =>
                        setConfig({
                          ...config,
                          children: {
                            ...config.children,
                            bedSupplementCents: bedSupplementCents ?? 0,
                          },
                        })
                      }
                    />
                  )}
                </>
              )}
            </>
          )}
          {lodging.mode === 'CHOICE' && (
            <OptionEditor
              title="住宿方案"
              options={config.accommodation}
              onChange={(accommodation) => setConfig({ ...config, accommodation })}
              imagePicker={imagePicker}
            />
          )}
        </Row>
        <Row title="车辆安排">
          <label>
            安排方式
            <select
              value={transport.mode}
              onChange={(e) => {
                if (transport.mode === 'AUTO' || transport.mode === 'CHOICE')
                  vehicleDrafts.current[transport.mode] = config.vehicles;
                const next = e.target.value as typeof transport.mode;
                setConfig({
                  ...config,
                  transport: { ...transport, mode: next },
                  vehicles:
                    next === 'NONE' || next === 'INCLUDED'
                      ? []
                      : (vehicleDrafts.current[next] ??
                        config.vehicles.map((v) => ({
                          ...v,
                          ...(next === 'AUTO' ? { priceCents: 0 } : {}),
                        }))),
                });
              }}
            >
              <option value="NONE">无需车辆安排</option>
              <option value="INCLUDED">固定车型，已包含</option>
              <option value="AUTO">按人数自动配车，已包含</option>
              <option value="CHOICE">展示车型，用户自行选择</option>
            </select>
          </label>
          {transport.mode !== 'NONE' && (
            <label className="booking-editor__grow">
              车辆说明
              <input
                aria-label="车辆说明"
                value={transport.summary}
                placeholder="车辆安排将显示在人数下方"
                onChange={(e) =>
                  setConfig({ ...config, transport: { ...transport, summary: e.target.value } })
                }
              />
            </label>
          )}
          {['AUTO', 'CHOICE'].includes(transport.mode) && (
            <OptionEditor
              title={transport.mode === 'AUTO' ? '配车规则' : '车型'}
              auto={transport.mode === 'AUTO'}
              capacity
              options={config.vehicles}
              onChange={(vehicles) => setConfig({ ...config, vehicles })}
              imagePicker={imagePicker}
            />
          )}
        </Row>
        <Row title="增值服务">
          <OptionEditor
            title="可选服务"
            allowRoomNight={lodging.mode !== 'NONE'}
            options={config.extras}
            onChange={(extras) => setConfig({ ...config, extras })}
            imagePicker={imagePicker}
          />
          <small>前端默认不勾选，用户明确选择后计价。</small>
        </Row>
        <Row title="班期与价格">
          <p className="booking-editor__hint">
            普通日期使用服务日历与套餐价；下方仅维护特殊日期。限额按此套餐跨小程序合计占位，待付款订单也占位，关闭或退款完成后释放。
          </p>
          <div className="booking-departures">
            {(config.departures ?? []).map((d, i) => {
              const patch = (values: Partial<typeof d>) =>
                setConfig({
                  ...config,
                  departures: config.departures!.map((row, n) =>
                    n === i ? { ...row, ...values } : row,
                  ),
                });
              return (
                <div className="booking-departure" key={i}>
                  <label>
                    日期
                    <input
                      type="date"
                      value={d.date}
                      onChange={(e) => patch({ date: e.target.value })}
                    />
                  </label>
                  <label>
                    状态
                    <select
                      value={d.status}
                      onChange={(e) => patch({ status: e.target.value as typeof d.status })}
                    >
                      <option value="OPEN">可预订</option>
                      <option value="CONFIRM">需二次确认</option>
                      <option value="CLOSED">停售</option>
                    </select>
                  </label>
                  <Money
                    label="成人 / 套餐价"
                    value={d.priceCents}
                    placeholder="沿用基础价"
                    onChange={(priceCents) => patch({ priceCents })}
                  />
                  {config.children.enabled && (
                    <Money
                      label="儿童价"
                      value={d.childPriceCents}
                      placeholder="沿用儿童价"
                      onChange={(childPriceCents) => patch({ childPriceCents })}
                    />
                  )}
                  <label>
                    人数限额
                    <input
                      type="number"
                      min="0"
                      max="9999"
                      placeholder="不限额"
                      value={d.capacity ?? ''}
                      onChange={(e) =>
                        patch({ capacity: e.target.value === '' ? null : Number(e.target.value) })
                      }
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      setConfig({
                        ...config,
                        departures: config.departures!.filter((_, n) => n !== i),
                      })
                    }
                  >
                    移除
                  </button>
                </div>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() =>
              setConfig({
                ...config,
                departures: [
                  ...(config.departures ?? []),
                  {
                    date: '',
                    status: 'OPEN',
                    priceCents: null,
                    childPriceCents: null,
                    capacity: null,
                  },
                ],
              })
            }
          >
            ＋ 特殊班期
          </button>
        </Row>
        <Row title="页面素材">
          {imagePicker('下单页头图', config.hero?.imageUrl ?? '', (imageUrl) =>
            setConfig({ ...config, hero: { title: '', subtitle: '', ...config.hero, imageUrl } }),
          )}
          <label>
            主标题
            <textarea
              rows={2}
              maxLength={50}
              value={config.hero?.title ?? ''}
              placeholder="留空使用线路名称"
              onChange={(e) =>
                setConfig({
                  ...config,
                  hero: { imageUrl: '', subtitle: '', ...config.hero, title: e.target.value },
                })
              }
            />
          </label>
          <label>
            副标题
            <input
              maxLength={100}
              value={config.hero?.subtitle ?? ''}
              placeholder="留空使用线路简介"
              onChange={(e) =>
                setConfig({
                  ...config,
                  hero: { imageUrl: '', title: '', ...config.hero, subtitle: e.target.value },
                })
              }
            />
          </label>
        </Row>
        {mediaError && <p role="alert">{mediaError}</p>}
      </fieldset>
    </section>
  );
}
function Row({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="booking-editor__row">
      <h3>{title}</h3>
      <div className="booking-editor__fields">{children}</div>
    </div>
  );
}
function Money({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  placeholder?: string;
}) {
  return (
    <label>
      {label}
      <input
        type="number"
        min="0"
        step="0.01"
        placeholder={placeholder}
        value={value === null ? '' : value / 100}
        onChange={(e) =>
          onChange(e.target.value === '' ? null : Math.round(Number(e.target.value) * 100))
        }
      />
    </label>
  );
}
function OptionEditor({
  title,
  options,
  onChange,
  imagePicker,
  auto = false,
  capacity = false,
  allowRoomNight = false,
}: {
  title: string;
  options: BookingOption[];
  onChange: (options: BookingOption[]) => void;
  imagePicker: (label: string, url: string, onChange: (url: string) => void) => ReactNode;
  auto?: boolean;
  capacity?: boolean;
  allowRoomNight?: boolean;
}) {
  const patch = (id: string, values: Partial<BookingOption>) =>
    onChange(options.map((o) => (o.id === id ? { ...o, ...values } : o)));
  return (
    <div className="booking-editor__options">
      {options.map((option) => (
        <div className="booking-editor__option" key={option.id}>
          {imagePicker(`${title}配图`, option.imageUrl ?? '', (imageUrl) =>
            patch(option.id, { imageUrl }),
          )}
          <label>
            名称
            <input
              maxLength={60}
              value={option.title}
              onChange={(e) => patch(option.id, { title: e.target.value })}
            />
          </label>
          <label className="booking-editor__grow">
            说明
            <input
              maxLength={200}
              value={option.description}
              onChange={(e) => patch(option.id, { description: e.target.value })}
            />
          </label>
          {(auto || capacity) && (
            <>
              <label>
                最少人数
                <input
                  type="number"
                  min="1"
                  max="99"
                  value={option.minPeople ?? 1}
                  onChange={(e) => patch(option.id, { minPeople: Number(e.target.value) })}
                />
              </label>
              <label>
                最多人数
                <input
                  type="number"
                  min="1"
                  max="99"
                  value={option.maxPeople ?? (auto ? 4 : 99)}
                  onChange={(e) => patch(option.id, { maxPeople: Number(e.target.value) })}
                />
              </label>
            </>
          )}
          {!auto && (
            <>
              <Money
                label="加价（元）"
                value={option.priceCents}
                onChange={(priceCents) => patch(option.id, { priceCents: priceCents ?? 0 })}
              />
              <label>
                计费单位
                <select
                  value={option.charge}
                  onChange={(e) =>
                    patch(option.id, { charge: e.target.value as BookingOption['charge'] })
                  }
                >
                  <option value="ORDER">每单</option>
                  <option value="PERSON">每位出行人</option>
                  <option value="ADULT">每名成人</option>
                  {(title === '住宿方案' || allowRoomNight || option.charge === 'ROOM_NIGHT') && (
                    <option value="ROOM_NIGHT">每间每晚</option>
                  )}
                </select>
              </label>
            </>
          )}
          <button type="button" onClick={() => onChange(options.filter((o) => o.id !== option.id))}>
            移除
          </button>
        </div>
      ))}
      <button
        type="button"
        disabled={options.length >= 20}
        onClick={() =>
          onChange([
            ...options,
            {
              id: crypto.randomUUID(),
              title: '',
              description: '',
              priceCents: 0,
              charge: 'ORDER',
              ...(auto || capacity ? { minPeople: 1, maxPeople: 4 } : {}),
            },
          ])
        }
      >
        ＋ 添加{title}
      </button>
      {options.length > 0 && !auto && <small>0 元表示套餐已含；付费选项须由用户主动选择。</small>}
    </div>
  );
}
